
import * as d3 from 'd3';
import { createNumberTooltip, formatValue, GroupedValueNameData, roundDigitsAfterDecimal, sidelyChartColors, ValueNameData } from '../cross_platform/dashboard_script';
import { BorderColor2, DarkGreySidely2, GreenSidely, GreySidely3, RedSidely } from '../../../styles/global/css/Utils';
import { TitleFontFamily } from '../../../styles/global/css/GlobalText';

export function createNumberDiv({
	chartData,
	width,
	height,
	margin,
	granularity,
	opts,
	tooltipId
} : {
	chartData: ValueNameData | GroupedValueNameData[], 
	width: number, 
	height: number, 
	margin: number,
	granularity?: string,
	opts?: {
		locale?: string,
		number_extension?: string | number,
		digits_after_decimal?: number,
		display_line_chart_evolution?: boolean,
		is_percentage?: boolean
	},
	tooltipId?: string
}): { container: HTMLDivElement; cleanup: () => void } | null {
	const sparkline = Boolean(opts?.display_line_chart_evolution);
	const container = document.createElement('div');
	container.style.width = `${width}px`;
	container.style.height = sparkline ? `${height}px` : 'auto';
	container.style.display = 'flex';
	container.style.flexDirection = 'column';
	container.style.justifyContent = 'center';
	container.style.alignItems = 'center';
	container.style.position = 'relative';
	container.style.margin = sparkline ? `${margin}px` : `${margin / 2}px 0 0`;
	container.style.fontFamily = '\'Inter\', sans-serif';
	const mainFontSize = 33;

	if (!Array.isArray(chartData)) {
		const dataPoint = chartData;
		const valueDiv = document.createElement('div');
		valueDiv.style.display = 'flex';
		valueDiv.style.alignItems = 'center';
		valueDiv.style.justifyContent = 'center';
		valueDiv.style.width = '100%';
		valueDiv.style.fontWeight = '500';
		valueDiv.style.lineHeight = '1.2';
		valueDiv.style.letterSpacing = '-0.01em';
		valueDiv.style.fontFamily = TitleFontFamily;
		valueDiv.className = 'number-chart-value';
		valueDiv.style.fontSize = `${mainFontSize}px`;
		valueDiv.textContent = formatValue(roundDigitsAfterDecimal(dataPoint.value, opts?.digits_after_decimal), opts);
		container.appendChild(valueDiv);

		const { cleanup } = createNumberTooltip(
			container,
			() => `<div><span style="opacity:0.9">${dataPoint.name}</span> : <span style="font-weight:600">${formatValue(roundDigitsAfterDecimal(dataPoint.value, opts?.digits_after_decimal), opts)}</span></div>`,
			{ tooltipId }
		);

		return { container, cleanup };
	}
	else if (Array.isArray(chartData) && chartData.length > 0) {
		// Date parsing/formatting helpers (available to tooltip and labels)
		const gran = granularity ?? 'month';
		const locale = opts?.locale ?? 'fr-FR';

		const tryParseDate = (s: string): Date | null => {
			const d = new Date(s);
			if (isNaN(d.getTime())) return null;
			return d;
		};

		// Detect whether names are dates to decide between evolution and sum display
		const namesAreDates = chartData.every(d => typeof d.name === 'string' && tryParseDate(d.name) !== null);

		if (!namesAreDates) {
			// Sum all values and display as a plain big number
			const total = chartData.reduce((acc, d) => acc + (typeof d.value === 'number' ? d.value : 0), 0);

			const valueDiv = document.createElement('div');
			valueDiv.style.display = 'flex';
			valueDiv.style.alignItems = 'center';
			valueDiv.style.justifyContent = 'center';
			valueDiv.style.width = '100%';
			valueDiv.style.fontWeight = '500';
			valueDiv.style.lineHeight = '1.2';
			valueDiv.style.letterSpacing = '-0.01em';
			valueDiv.style.fontFamily = TitleFontFamily;
			valueDiv.className = 'number-chart-value';
			valueDiv.style.fontSize = `${mainFontSize}px`;
			valueDiv.textContent = formatValue(roundDigitsAfterDecimal(total, opts?.digits_after_decimal), opts);
			container.appendChild(valueDiv);

			const { cleanup } = createNumberTooltip(
				container,
				() => chartData
					.map(d => `<div><span style="opacity:0.9">${d.name}</span> : <span style="font-weight:600">${formatValue(roundDigitsAfterDecimal(d.value, opts?.digits_after_decimal), opts)}</span></div>`)
					.join(''),
				{ tooltipId }
			);

			return { container, cleanup };
		}

		// Evolution (first -> last) display
		const first = chartData[0];
		const last = chartData[chartData.length - 1];

		const containterDiv = document.createElement('div');
		containterDiv.style.width = '100%';
		containterDiv.style.height = '100%';
		containterDiv.style.display = 'flex';
		containterDiv.style.flexDirection = 'column';
		containterDiv.style.justifyContent = 'center';
		containterDiv.style.alignItems = 'center';
		containterDiv.style.position = 'relative';
		container.appendChild(containterDiv);

		const valueDiv = document.createElement('div');
		valueDiv.style.display = 'flex';
		valueDiv.style.alignItems = 'center';
		valueDiv.style.justifyContent = 'center';
		valueDiv.style.height = sparkline ? '30%' : 'auto';
		valueDiv.style.paddingTop = sparkline ? '4px' : '0';
		valueDiv.style.fontWeight = '500';
		valueDiv.style.lineHeight = '1.2';
		valueDiv.style.letterSpacing = '-0.01em';
		valueDiv.style.fontFamily = TitleFontFamily;
		valueDiv.className = 'number-chart-value';
		valueDiv.style.fontSize = `${mainFontSize}px`;
		valueDiv.textContent = formatValue(roundDigitsAfterDecimal(last.value, opts?.digits_after_decimal), opts);

		// Compute percentage change safely
		let evolutionText = 'N/A';
		let evolutionSign = '';
		let evoPercentNum: number | null = null;
		if (typeof first.value === 'number' && typeof last.value === 'number') {
			if (opts?.is_percentage) {
				// For percentage values, show raw difference instead of percentage of percentage
				const diff = last.value - first.value;
				evoPercentNum = diff; // Store the raw diff for color logic
				const formatted = diff.toLocaleString(opts?.locale ?? 'fr-FR', { maximumFractionDigits: opts?.digits_after_decimal ?? 1 });
				evolutionSign = diff > 0 ? '+' : '';
				evolutionText = `${evolutionSign}${formatted}`;
			} else {
				if (first.value === 0) {
					// avoid division by zero; if both zero -> 0%, else N/A
					if (last.value === 0) {
						evoPercentNum = 0;
						evolutionText = '0%';
					} else {
						evolutionText = 'N/A';
					}
				} else {
					evoPercentNum = ((last.value - first.value) / Math.abs(first.value)) * 100;
					const formatted = evoPercentNum.toLocaleString(opts?.locale ?? 'fr-FR', { maximumFractionDigits: 1 });
					evolutionSign = evoPercentNum > 0 ? '+' : '';
					evolutionText = `${evolutionSign}${formatted}%`;
				}
			}
		}

		// Add small line chart if option is enabled
		let lineChartDiv: HTMLDivElement | null = null;
		if (opts?.display_line_chart_evolution && chartData.length >= 2) {
			lineChartDiv = document.createElement('div');
			lineChartDiv.style.width = '100%';
			lineChartDiv.style.height = '50%';
			lineChartDiv.style.display = 'flex';
			lineChartDiv.style.justifyContent = 'center';
			lineChartDiv.style.alignItems = 'center';
			lineChartDiv.style.position = 'relative'; // Enable absolute positioning for children


			// Create small SVG line chart - use more height
			const chartMargin = { top: 10, right: 20, bottom: 10, left: 20 };
			const chartWidth = width * 0.9;
			const chartHeight = Math.min(height * 0.50, 150);
			const innerWidth = chartWidth - chartMargin.left - chartMargin.right;
			const innerHeight = chartHeight - chartMargin.top - chartMargin.bottom;
			
			const svg = d3.create('svg')
				.attr('width', chartWidth)
				.attr('height', chartHeight)
				.attr('viewBox', [0, 0, chartWidth, chartHeight])
				.style('overflow', 'visible');

			// Add gradient definitions
			const defs = svg.append('defs');
			const color = sidelyChartColors[0];
			const gradId = `evolution-gradient-${Math.random().toString(36).substr(2, 9)}`;
			const lg = defs.append('linearGradient')
				.attr('id', gradId)
				.attr('x1', '0')
				.attr('y1', '0')
				.attr('x2', '0')
				.attr('y2', '1')
				.attr('gradientUnits', 'objectBoundingBox');
			lg.append('stop').attr('offset', '0%').attr('stop-color', color).attr('stop-opacity', '0.35');
			lg.append('stop').attr('offset', '100%').attr('stop-color', color).attr('stop-opacity', '0');

			// Extract values and create scales
			const values = chartData.map(d => d.value);
			const minValue = Math.min(...values);
			const maxValue = Math.max(...values);
			const valueRange = maxValue - minValue;
			
			// Create proper scale even for flat lines
			const yScale = d3.scaleLinear()
				.domain(valueRange === 0 ? [minValue - 1, minValue + 1] : [minValue, maxValue])
				.range([chartMargin.top + innerHeight, chartMargin.top]);

			const xScale = d3.scaleLinear()
				.domain([0, chartData.length - 1])
				.range([chartMargin.left, chartMargin.left + innerWidth]);

			// Add x-axis with ticks but no labels
			const xAxis = d3.axisBottom(xScale)
				.ticks(Math.min(5, chartData.length))
				.tickSize(3)
				.tickFormat(() => '');
			
			svg.append('g')
				.attr('transform', `translate(0,${chartMargin.top + innerHeight})`)
				.call(xAxis)
				.call(g => g.select('.domain').attr('stroke', BorderColor2))
				.call(g => g.selectAll('.tick line').attr('stroke', BorderColor2));

			// Add y-axis with ticks but no labels  
			const yAxis = d3.axisLeft(yScale)
				.ticks(3)
				.tickSize(3)
				.tickFormat(() => '');
			
			svg.append('g')
				.attr('transform', `translate(${chartMargin.left},0)`)
				.call(yAxis)
				.call(g => g.select('.domain').attr('stroke', BorderColor2))
				.call(g => g.selectAll('.tick line').attr('stroke', BorderColor2));

			// Create line generator
			const line = d3.line<ValueNameData>()
				.x((d, i) => xScale(i))
				.y(d => yScale(d.value))
				.curve(d3.curveMonotoneX);

			// Create area generator for gradient fill
			const area = d3.area<ValueNameData>()
				.x((d, i) => xScale(i))
				.y0(chartMargin.top + innerHeight)
				.y1(d => yScale(d.value))
				.curve(d3.curveMonotoneX);

			// Add the area fill with gradient
			svg.append('path')
				.datum(chartData)
				.attr('class', 'd3-area')
				.attr('fill', `url(#${gradId})`)
				.attr('d', area)
				.style('pointer-events', 'none');

			// Add the line path
			svg.append('path')
				.datum(chartData)
				.attr('fill', 'none')
				.attr('stroke', color)
				.attr('stroke-width', 1.5)
				.attr('d', line);

			// Add start and end dots
			svg.append('circle')
				.attr('cx', xScale(0))
				.attr('cy', yScale(chartData[0].value))
				.attr('r', 1.5)
				.attr('fill', DarkGreySidely2);

			svg.append('circle')
				.attr('cx', xScale(chartData.length - 1))
				.attr('cy', yScale(last.value))
				.attr('r', 1.5)
				.attr('fill', color);

			lineChartDiv.appendChild(svg.node()!);

			// Add value labels as HTML elements positioned absolutely over the SVG
			const startValueText = formatValue(roundDigitsAfterDecimal(chartData[0].value, opts?.digits_after_decimal), opts);
			const endValueText = formatValue(roundDigitsAfterDecimal(last.value, opts?.digits_after_decimal), opts);

			// Helper function to create positioned labels
			const createLabel = (xPos: number, yPos: number, backgroundColor: string, textContent: string) => {
				const label = document.createElement('div');
				label.style.position = 'absolute';
				label.style.left = `${xPos}px`;
				label.style.top = `${yPos - 30}px`;
				label.style.background = backgroundColor;
				label.style.color = 'white';
				label.style.padding = '2px 4px';
				label.style.borderRadius = '4px';
				label.style.fontSize = '10px';
				label.style.fontWeight = '600';
				label.style.whiteSpace = 'nowrap';
				label.style.pointerEvents = 'none';
				label.textContent = textContent;
				return label;
			};

			// Create and append labels
			const startLabel = createLabel(xScale(0), yScale(chartData[0].value), DarkGreySidely2, startValueText);
			const endLabel = createLabel(xScale(chartData.length - 1), yScale(last.value), color, endValueText);
			
			lineChartDiv.appendChild(startLabel);
			lineChartDiv.appendChild(endLabel);
		}

		const formatByGranularity = (dateStr: string): string => {
			const d = tryParseDate(dateStr);
			if (!d) return dateStr;
			if (gran === 'year') {
				return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(d);
			}
			if (gran === 'day') {
				return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
			}
			// default to month (include year for clarity)
			let formatted = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(d);
			formatted = formatted.replace(/\.$/, '');
			return formatted;
		};

		const percentDiv = document.createElement('div');
		percentDiv.style.fontSize = '12px';
		percentDiv.style.fontWeight = '500';
		percentDiv.style.lineHeight = '1';
		percentDiv.style.whiteSpace = 'nowrap';
		percentDiv.style.color = evoPercentNum !== null && evoPercentNum > 0 ? GreenSidely
			: evoPercentNum !== null && evoPercentNum < 0 ? RedSidely
				: DarkGreySidely2;
		percentDiv.textContent = evolutionText;

		const hasDates = typeof first.name === 'string' && typeof last.name === 'string';
		const datesDiv = document.createElement('div');
		datesDiv.style.fontSize = '12px';
		datesDiv.style.lineHeight = '1';
		datesDiv.style.color = GreySidely3;
		datesDiv.style.whiteSpace = 'nowrap';
		datesDiv.style.overflow = 'hidden';
		datesDiv.style.textOverflow = 'ellipsis';
		if (hasDates) datesDiv.textContent = `${formatByGranularity(first.name)} - ${formatByGranularity(last.name)}`;

		const row = document.createElement('div');
		row.style.display = 'flex';
		row.style.alignItems = 'center';
		row.style.pointerEvents = 'none';
		if (sparkline) {
			row.style.position = 'absolute';
			row.style.top = '25px';
			row.style.left = '10px';
			row.style.right = '10px';
			row.style.justifyContent = 'space-between';
			row.style.padding = '0 20%';
			row.appendChild(datesDiv);
			row.appendChild(percentDiv);
			containterDiv.appendChild(row);
			containterDiv.appendChild(valueDiv);
			if (lineChartDiv) containterDiv.appendChild(lineChartDiv);
		} else {
			row.style.gap = '6px';
			row.style.marginTop = '6px';
			row.style.maxWidth = '100%';
			row.style.justifyContent = 'center';
			row.appendChild(percentDiv);
			if (hasDates) row.appendChild(datesDiv);
			containterDiv.appendChild(valueDiv);
			containterDiv.appendChild(row);
		}

		// Create reusable tooltip for evolution display
		const { cleanup } = createNumberTooltip(
			container,
			() => {
				const diff = (typeof last.value === 'number' && typeof first.value === 'number') ? (last.value - first.value) : null;
				const diffText = diff !== null ? formatValue(roundDigitsAfterDecimal(diff, opts?.digits_after_decimal), opts) : 'N/A';
				const percentText = evolutionText;

				// Use the formatter so tooltip dates match the displayed granularity format
				let startFormatted = first.name;
				let endFormatted = last.name;
				try {
					startFormatted = formatByGranularity(first.name);
					endFormatted = formatByGranularity(last.name);
				} catch (_e) {
					// fallback to raw names
				}

				return `
					<div><span style="opacity:0.9">${startFormatted}</span> : <span style="font-weight:600">${typeof first.value === 'number' ? formatValue(roundDigitsAfterDecimal(first.value, opts?.digits_after_decimal), opts) : first.value}</span></div>
					<div><span style="opacity:0.9">${endFormatted}</span> : <span style="font-weight:600">${typeof last.value === 'number' ? formatValue(roundDigitsAfterDecimal(last.value, opts?.digits_after_decimal), opts) : last.value}</span></div>
					${opts?.is_percentage 
		? `<div><span style="opacity:0.9">Evolution</span> : <span style="font-weight:600">${percentText}</span></div>`
		: `<div><span style="opacity:0.9">Evolution (%)</span> : <span style="font-weight:600">${percentText}</span></div>
					<div><span style="opacity:0.9">Evolution (brute)</span> : <span style="font-weight:600">${diffText}</span></div>`
}
				`;
			},
			{ tooltipId }
		);

		return { container, cleanup };
	}
	
	return null;
}